/**
 * Per-month invoice analysis PDF — Pro-tier downloadable artefact.
 * Inputs are already-fetched data (no extra API calls):
 *   month       e.g. "2026-04"
 *   label       e.g. "April 2026"
 *   rows        invoiceHistory rows for that month
 *   timelineRow report.timeline[i] row for that month — { total, <Category>: amount, ... }
 *
 * Produces a single-page A4 PDF that the user can WhatsApp / share / print.
 */
import jsPDF from "jspdf";

const SAFFRON = [255, 107, 0];
const TEAL = [29, 158, 117];
const TEXT = [240, 237, 232];
const MUTED = [136, 136, 136];
const LINE = [42, 42, 53];
const BG = [10, 10, 15];

const PALETTE = [
  [255, 107, 0], // saffron
  [29, 158, 117], // teal
  [55, 138, 221], // blue
  [186, 117, 23], // amber
  [226, 75, 74], // red
  [157, 60, 255], // purple
  [165, 205, 57], // lime
  [252, 128, 25], // orange
];

const fmtINR = (n) =>
  `Rs. ${new Intl.NumberFormat("en-IN").format(Math.max(0, Math.round(n || 0)))}`;

const setFill = (doc, [r, g, b]) => doc.setFillColor(r, g, b);
const setText = (doc, [r, g, b]) => doc.setTextColor(r, g, b);
const setDraw = (doc, [r, g, b]) => doc.setDrawColor(r, g, b);

// Strip non-Latin1 chars (jsPDF default font can't render rupee symbol etc.)
const safe = (s) => String(s ?? "").replace(/[^\x20-\x7E]/g, "");

export function generateMonthlyReportPdf({
  month,
  label,
  rows = [],
  timelineRow = null,
  totalSpend = null,
  filename = null,
}) {
  const doc = new jsPDF({ unit: "pt", format: "a4" });
  const W = doc.internal.pageSize.getWidth();
  const H = doc.internal.pageSize.getHeight();
  const M = 36; // margin

  // Background
  setFill(doc, BG);
  doc.rect(0, 0, W, H, "F");

  // ---------- Header band ----------
  setFill(doc, SAFFRON);
  doc.rect(0, 0, W, 4, "F");

  setText(doc, MUTED);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(8);
  doc.text("CARTIQ 360  ·  BY SOLUTIONIST", M, 30);

  setText(doc, TEXT);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(22);
  doc.text(`Invoices uploaded for ${safe(label)}`, M, 60);

  setText(doc, MUTED);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  const totalsLine = `${rows.length} ${rows.length === 1 ? "invoice" : "invoices"} processed   ·   month ${safe(month)}`;
  doc.text(totalsLine, M, 78);

  // ---------- Stats row ----------
  const monthTotal =
    typeof totalSpend === "number"
      ? totalSpend
      : Number(timelineRow?.total) || 0;

  const platforms = new Set(
    rows.map((r) => (r.platform || "").trim()).filter((p) => p && p !== "Other")
  );
  const items = rows.reduce((s, r) => s + (Number(r.items_extracted) || 0), 0);

  // Top category from timelineRow
  const categories = timelineRow
    ? Object.entries(timelineRow)
        .filter(([k, v]) => !["month", "label", "total"].includes(k) && typeof v === "number")
        .sort((a, b) => b[1] - a[1])
    : [];
  const topCategory = categories[0]?.[0] || "—";

  const statY = 110;
  const statW = (W - M * 2 - 24) / 4;
  const stats = [
    { label: "Total spent", value: fmtINR(monthTotal), accent: SAFFRON },
    { label: "Invoices", value: String(rows.length), accent: TEAL },
    { label: "Platforms", value: String(platforms.size || "—"), accent: PALETTE[2] },
    { label: "Items", value: String(items), accent: PALETTE[3] },
  ];
  stats.forEach((s, i) => {
    const x = M + i * (statW + 8);
    setDraw(doc, LINE);
    doc.setLineWidth(0.5);
    doc.rect(x, statY, statW, 56);
    // accent strip
    setFill(doc, s.accent);
    doc.rect(x, statY, 3, 56, "F");
    setText(doc, MUTED);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(7.5);
    doc.text(s.label.toUpperCase(), x + 12, statY + 18);
    setText(doc, TEXT);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(15);
    doc.text(safe(s.value), x + 12, statY + 42);
  });

  // ---------- Top category line ----------
  setText(doc, MUTED);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(8);
  doc.text("TOP CATEGORY", M, 200);
  setText(doc, SAFFRON);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(13);
  doc.text(safe(topCategory).toUpperCase(), M, 218);

  // ---------- Category breakdown bars ----------
  if (categories.length) {
    const sectionY = 248;
    setText(doc, MUTED);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(8);
    doc.text("WHERE IT WENT  ·  CATEGORY BREAKDOWN", M, sectionY);

    const barAreaY = sectionY + 14;
    const total = monthTotal || 1;
    const slice = categories.slice(0, 6);
    slice.forEach((c, i) => {
      const [name, amt] = c;
      const y = barAreaY + i * 26;
      const pct = Math.max(0, Math.min(100, Math.round((amt / total) * 100)));
      const colour = PALETTE[i % PALETTE.length];

      setText(doc, TEXT);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(9);
      doc.text(safe(name).toUpperCase(), M, y);

      setText(doc, MUTED);
      doc.setFont("helvetica", "normal");
      doc.setFontSize(9);
      const right = `${fmtINR(amt)}   (${pct}%)`;
      doc.text(right, W - M - doc.getTextWidth(right), y);

      // bar
      const barW = W - M * 2;
      setFill(doc, LINE);
      doc.rect(M, y + 4, barW, 6, "F");
      setFill(doc, colour);
      doc.rect(M, y + 4, Math.round(barW * (pct / 100)), 6, "F");
    });
  }

  // ---------- Invoice list ----------
  const listTop = 248 + 14 + Math.min(6, categories.length) * 26 + 20;
  setText(doc, MUTED);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(8);
  doc.text("UPLOAD LEDGER", M, listTop);

  const headerY = listTop + 14;
  setText(doc, MUTED);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(7.5);
  const cols = [
    { x: M, w: W * 0.42 - M, label: "INVOICE" },
    { x: W * 0.42, w: W * 0.18, label: "PLATFORM" },
    { x: W * 0.6, w: W * 0.18, label: "DATE" },
    { x: W * 0.78, w: W - M - W * 0.78, label: "ITEMS" },
  ];
  cols.forEach((c) => doc.text(c.label, c.x, headerY));
  setDraw(doc, LINE);
  doc.setLineWidth(0.5);
  doc.line(M, headerY + 4, W - M, headerY + 4);

  const maxRows = Math.floor((H - headerY - 90) / 18);
  const visibleRows = rows.slice(0, maxRows);
  visibleRows.forEach((r, i) => {
    const y = headerY + 20 + i * 18;
    const fname = safe((r.filename || "—").replace(/\.(pdf|jpe?g|png|webp)$/i, ""));
    const trimmed = fname.length > 38 ? fname.slice(0, 37) + "…" : fname;
    setText(doc, TEXT);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.text(trimmed, cols[0].x, y);

    setText(doc, MUTED);
    doc.text(safe(r.platform || "—"), cols[1].x, y);

    const d = r.upload_date ? new Date(r.upload_date) : null;
    const dateStr =
      d && !Number.isNaN(d.getTime())
        ? `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}/${d.getFullYear()}`
        : "—";
    doc.text(dateStr, cols[2].x, y);

    setText(doc, TEXT);
    doc.setFont("helvetica", "bold");
    doc.text(String(r.items_extracted || 0), cols[3].x, y);
  });

  if (rows.length > maxRows) {
    setText(doc, MUTED);
    doc.setFont("helvetica", "italic");
    doc.setFontSize(8);
    doc.text(
      `+ ${rows.length - maxRows} more invoices in this month`,
      M,
      headerY + 24 + maxRows * 18
    );
  }

  // ---------- Footer ----------
  setDraw(doc, LINE);
  doc.line(M, H - 50, W - M, H - 50);
  setText(doc, MUTED);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(8);
  doc.text(
    `Make your friends curious. Share this app -> cartiq360.in`,
    M,
    H - 32
  );
  setText(doc, SAFFRON);
  doc.text("CARTIQ 360", W - M - doc.getTextWidth("CARTIQ 360"), H - 32);

  const out = filename || `cartiq360-${month}.pdf`;
  doc.save(out);
}
